import React, { Component } from 'react';
import {
  Box, Button, Select, MenuItem, FormControl, Grid, Typography,
  Snackbar, Alert, SelectChangeEvent, Dialog, DialogContent, DialogContentText
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { decodeJwt, UserRole } from '../Pages/ApiCommonService';
import { getbroker, fetchCSRUsersByBroker } from '../ApiSevices/UserMasterApis';
import { GridTable } from '../../Components/Sidebar/SidebarStyles';

interface ChecklistTemplateProps {
  displaysidebar: boolean;
  basePath: string;
}

interface Broker {
  Broker_ID: number;
  vchBrokerName: string;
}

interface CSR {
  ID: number,
  vchEmail: string;
}

interface CsrUploadState {
  broker: string;
  csr: string;
  csrId: number;
  secondaryCSR: string;
  secondaryCSRId: number;
  brokers: Broker[];
  csrs: CSR[];
  snackbarOpen: boolean;
  openConfirmDialog: boolean;
  loading: boolean;
  snackbarMessage: string;
  snackbarSeverity: 'success' | 'error';
}

class CsrUploadDocument extends Component<ChecklistTemplateProps, CsrUploadState> {
  private brokerMap: Map<string, Broker>;
  private _isMounted: boolean;

  constructor(props: ChecklistTemplateProps) {
    super(props);
    this.state = {
      broker: '',
      csr: '',
      csrId: 0,
      secondaryCSR: '',
      secondaryCSRId: 0,
      brokers: [],
      csrs: [],
      snackbarOpen: false,
      openConfirmDialog: false,
      loading: true,
      snackbarMessage: '',
      snackbarSeverity: 'success'
    };

    this.brokerMap = new Map();
    this._isMounted = false;
  }

  async componentDidMount() {
    this._isMounted = true;

    const targetPath = '/jobs/upload';
    if (window.location.pathname !== targetPath) {
      sessionStorage.removeItem('selectedBrokerId');
      sessionStorage.removeItem('selectedCSRUser');
      sessionStorage.removeItem('selectedCSRUserId');
      sessionStorage.removeItem('selectedSecondaryCSRUser');
      sessionStorage.removeItem('selectedSecondaryCSRUserId');
      sessionStorage.removeItem('enableUploadDocuments');
    }

    try {
      const brokers = await getbroker();
      if (this._isMounted) {
        const validBrokers = brokers.filter((b: Broker) => b?.vchBrokerName);
        validBrokers.forEach((b: Broker) => {
          this.brokerMap.set(b.vchBrokerName, b);
        });
        const sortedBrokers = validBrokers.sort((a: any, b: any) =>
          a.vchBrokerName.localeCompare(b.vchBrokerName)
        );
        this.setState({ brokers: sortedBrokers, loading: false });
      }
    } catch (err) {
      if (this._isMounted) {
        console.error('Error loading brokers', err);
        this.setState({ loading: false, snackbarMessage: 'Error loading brokers', snackbarSeverity: 'error', snackbarOpen: true });
      }
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  handleBrokerChange = async (event: SelectChangeEvent<string>) => {
    const broker = event.target.value;
    this.setState({ broker, csr: '', secondaryCSR: '', loading: true });

    const selectedBroker = this.brokerMap.get(broker);
    if (!selectedBroker) return;

    try {
      const csrUsers = await fetchCSRUsersByBroker(selectedBroker.Broker_ID);
      if (this._isMounted) {
        this.setState({ csrs: csrUsers || [], csr: '', secondaryCSR: '', loading: false });
      }
    } catch (err) {
      if (this._isMounted) {
        console.error('Error fetching CSR users', err);
        this.setState({ loading: false, snackbarMessage: 'Error loading CSR users', snackbarSeverity: 'error', snackbarOpen: true });
      }
    }
  };

  handleSaveClick = () => {
    this.setState({ openConfirmDialog: true });
  };

  handleConfirmSave = async () => {
    const { broker, csr, secondaryCSR, csrId, secondaryCSRId } = this.state;
    const { basePath } = this.props;
    const brokerObj = this.brokerMap.get(broker);
    if (!brokerObj || !csr) return;

    try {
      this.setState({ loading: true });

      const token = sessionStorage.getItem('token');
      let isMSUser = false;

      if (token) {
        const decodedToken = decodeJwt(token);
        if (decodedToken && decodedToken.RoleId === UserRole.MSUser) {
          isMSUser = true;
        }
      }
      sessionStorage.setItem('isMSUser', isMSUser.toString());
      const targetPath = '/jobs/upload';

      if (isMSUser) {
        sessionStorage.setItem('selectedBrokerId', brokerObj.Broker_ID.toString());
        sessionStorage.setItem('selectedCSRUser', csr);
        sessionStorage.setItem('selectedCSRUserId', csrId ? csrId.toString() : "");

        // Store secondaryCSR if it's entered
        if (secondaryCSR && secondaryCSR.trim() !== '') {
          sessionStorage.setItem('selectedSecondaryCSRUser', secondaryCSR);
          sessionStorage.setItem('selectedSecondaryCSRUserId', secondaryCSRId ? secondaryCSRId.toString() : "");
        }
        sessionStorage.setItem('enableUploadDocuments', 'true');
      }

      window.location.href = basePath + targetPath;
    } catch (err) {
      if (this._isMounted) {
        console.error(err);
        this.setState({
          loading: false,
          snackbarMessage: 'Failed to save configuration',
          snackbarSeverity: 'error',
          snackbarOpen: true
        });
      }
    }
  };

  handleCloseConfirm = () => {
    this.setState({
      openConfirmDialog: false,
      broker: '',
      csr: '',
      secondaryCSR: '',
    });
  };

  handleCloseSnackbar = () => {
    this.setState({ snackbarOpen: false });
  };

  get isSaveDisabled() {
    const { broker, csr } = this.state;
    return !broker || !csr;
  }

  onCsrUserChange = (e: SelectChangeEvent) => {
    if (e?.target?.value) {
      const userData = this.state.csrs.find((f: any) => f.vchEmail === e.target.value)
      this.setState({ csr: e.target.value, csrId: userData?.ID || 0 })
    } else {
      this.setState({ csr: '', csrId: 0 })
    }
  }

  onSecondaryCsrUserChange = (e: SelectChangeEvent) => {
    if (e?.target?.value) {
      const userData = this.state.csrs.find((f: any) => f.vchEmail === e.target.value)
      this.setState({ secondaryCSR: e.target.value, secondaryCSRId: userData?.ID || 0 })
    }
    else {
      this.setState({ secondaryCSR: '', secondaryCSRId: 0 })
    }
  }

  render() {
    const { broker, csr, secondaryCSR, brokers, csrs, snackbarOpen, snackbarMessage, snackbarSeverity, openConfirmDialog, loading } = this.state;
    const { displaysidebar } = this.props;

    return (
      <GridTable displaysidebar={displaysidebar}>
        {/* Header Section */}
        <Box sx={{
          padding: '2px 0',
          backgroundColor: '#f8f8f8',
          borderBottom: '1px solid #ddd',
          marginBottom: '25px'
        }}>
          <Typography variant="subtitle2" sx={{
            fontWeight: 'bold',
            color: '#000',
            ml: 1,
            textTransform: 'uppercase'
          }}>
            CSR | UPLOAD DOCUMENT
          </Typography>
        </Box>

        <Box sx={{
          p: 2,
          width: '100%',
          maxWidth: { xs: '100%', sm: '700px', md: '560px' },
          backgroundColor: '#fff',
          border: '2px solid #ddd'
        }}>
          {/* Broker */}
          <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Grid item xs={12} sm={2} md={3}>
              <Typography sx={{
                fontSize: '12px',
                fontWeight: 500,
                color: '#000',
                whiteSpace: 'nowrap'
              }}>
                Select Broker <Box component="span" sx={{ color: 'red' }}>*</Box>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4} md={6}>
              <FormControl fullWidth size="small">
                <Select
                  value={broker}
                  displayEmpty
                  onChange={this.handleBrokerChange}
                  sx={{
                    fontSize: '12px',
                    height: 36,
                    color: broker ? 'text.primary' : 'text.secondary'
                  }}
                >
                  <MenuItem value="" disabled sx={{ fontSize: '12px' }}>
                    Select Broker
                  </MenuItem>
                  {brokers.map(b => (
                    <MenuItem
                      key={b.Broker_ID}
                      value={b.vchBrokerName}
                      sx={{ fontSize: '12px' }}
                    >
                      {b.vchBrokerName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Primary CSR */}
          <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Grid item xs={12} sm={2} md={3}>
              <Typography sx={{
                fontSize: '12px',
                fontWeight: 500,
                color: '#000',
                whiteSpace: 'nowrap'
              }}>
                Primary CSR <Box component="span" sx={{ color: 'red' }}>*</Box>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4} md={6}>
              <FormControl fullWidth size="small">
                <Select
                  value={csr}
                  displayEmpty
                  onChange={(e) => this.onCsrUserChange(e)}// this.setState({ csr: e.target.value })}
                  sx={{
                    fontSize: '12px',
                    height: 36,
                    color: csr ? 'text.primary' : 'text.secondary'
                  }}
                >
                  <MenuItem value="" disabled sx={{ fontSize: '12px' }}>
                    Select CSR
                  </MenuItem>
                  {csrs.map((c, i) => (
                    <MenuItem
                      key={i}
                      value={c.vchEmail}
                      sx={{ fontSize: '12px' }}
                    >
                      {c.vchEmail}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Secondary CSR */}
          <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Grid item xs={12} sm={2} md={3}>
              <Typography sx={{
                fontSize: '12px',
                fontWeight: 500,
                color: '#000',
                whiteSpace: 'nowrap'
              }}>
                Secondary CSR
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4} md={6}>
              <FormControl fullWidth size="small">
                <Select
                  value={secondaryCSR}
                  displayEmpty
                  onChange={(e) => this.onSecondaryCsrUserChange(e)} // this.setState({ secondaryCSR: e.target.value })}
                  sx={{
                    fontSize: '12px',
                    height: 36,
                    color: secondaryCSR ? 'text.primary' : 'text.secondary'
                  }}
                >
                  <MenuItem value="" disabled sx={{ fontSize: '12px' }}>
                    Select Secondary CSR
                  </MenuItem>
                  {csrs.map((c, i) => (
                    <MenuItem
                      key={i}
                      value={c.vchEmail}
                      sx={{ fontSize: '12px' }}
                    >
                      {c.vchEmail}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Save Button */}
          <Box sx={{ textAlign: 'right', mt: 2 }}>
            <Button
              variant="contained"
              disabled={this.isSaveDisabled || loading}
              onClick={this.handleConfirmSave}
              sx={{
                backgroundColor: '#1e1466',
                fontSize: '11px',
                padding: '2px 12px',
                minWidth: 60,
                height: 28,
                '&:disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#a0a0a0'
                }
              }}
            >
              Save
            </Button>
          </Box>
        </Box>

        {/* Confirmation Dialog */}
        <Dialog open={openConfirmDialog} onClose={this.handleCloseConfirm}>
          <DialogContent sx={{
            padding: '0px 20px',
            paddingTop: '25px',
            paddingBottom: '35px',
            textAlign: 'center',
          }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 2,
            }}>
              <Box sx={{
                backgroundColor: '#ECFDF5',
                borderRadius: '50%',
                width: 60,
                height: 60,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <SaveIcon sx={{ color: '#22c55e', fontSize: 30 }} />
              </Box>
            </Box>

            <DialogContentText sx={{
              textAlign: 'center',
              mt: 1,
              mb: 3,
              fontWeight: 'bold',
              color: 'rgba(0, 0, 0, 0.7)',
              fontSize: '14px',
            }}>
              Are you sure you want to save the changes?
            </DialogContentText>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, height: '30px' }}>
              <Button
                onClick={this.handleConfirmSave}
                variant="contained"
                sx={{
                  px: 4,
                  borderRadius: '6px',
                  fontSize: '12px',
                  backgroundColor: '#1e1466',
                  '&:hover': {
                    backgroundColor: '#0f0c44',
                  },
                }}
                disabled={loading}
              >
                YES
              </Button>

              <Button
                onClick={this.handleCloseConfirm}
                sx={{
                  color: '#007bff',
                  border: '0.5px solid #53494987',
                  px: 4,
                  borderRadius: '6px',
                  fontSize: '12px',
                  '&:hover': {
                    borderColor: '#0056b3',
                    color: '#0056b3',
                  },
                }}
              >
                NO
              </Button>
            </Box>
          </DialogContent>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={this.handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={this.handleCloseSnackbar}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>

        {loading && (
          <Box sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.5)',
            zIndex: 9999
          }}>
            <div className="loader"></div>
          </Box>
        )}
      </GridTable>
    );
  }
}

export default CsrUploadDocument;